import { ChartConfig } from '@/components/ui/chart';

interface ChartDataItem {
  key: string;
  value: number;
  fill: string;
}

interface AccountingCenter {
  acmev?: number;
  acmev_used?: number;
  acrt?: number;
  acrt_achieved?: number;
}

export const calculateChartData = (acCenter: AccountingCenter | undefined, defaultColor = '#32ADE6') => {
  if (!acCenter) {
    return {
      acmevChartData: [{ key: 'remaining', value: 100, fill: defaultColor }],
      acrtChartData: [{ key: 'target', value: 100, fill: defaultColor }]
    };
  }

  // Calculate percentages for ACMEV (Accounting Center Maximum Expense Value)
  let acmevData = [];
  if (acCenter.acmev !== undefined) {
    const acmevTotal = acCenter.acmev || 0;
    const acmevUsed = acCenter.acmev_used || 0;
    const acmevRemaining = Math.max(0, acmevTotal - acmevUsed);

    // Only add remaining if there's a total budget value
    if (acmevTotal > 0) {
      const acmevRemainingPercent = Math.round((acmevRemaining / acmevTotal) * 100);
      acmevData.push({ key: 'remaining', value: acmevRemainingPercent, fill: defaultColor });

      const acmevUsedPercent = Math.round((acmevUsed / acmevTotal) * 100);
      acmevData.push({ key: 'spent', value: acmevUsedPercent, fill: '#FF2D55' });
    } else {
      // Default value if acmev is 0
      acmevData.push({ key: 'remaining', value: 100, fill: defaultColor });
    }
  } else {
    // Default value if acmev is not defined
    acmevData.push({ key: 'remaining', value: 100, fill: defaultColor });
  }

  // Calculate percentages for ACRT (Accounting Center Revenue Target)
  let acrtData = [];
  if (acCenter.acrt !== undefined) {
    const acrtTotal = acCenter.acrt || 0;
    const acrtAchieved = acCenter.acrt_achieved || 0;

    // Only calculate percentages if there's a target
    if (acrtTotal > 0) {
      const acrtAchievedPercent = Math.round((acrtAchieved / acrtTotal) * 100);

      // Add target (always 100%)
      acrtData.push({ key: 'target', value: 100 - acrtAchievedPercent, fill: defaultColor });
      acrtData.push({ key: 'achieved', value: acrtAchievedPercent, fill: '#34C759' });
    } else {
      // Default value if acrt is 0
      acrtData.push({ key: 'target', value: 100, fill: defaultColor });
    }
  } else {
    // Default value if acrt is not defined
    acrtData.push({ key: 'target', value: 100, fill: defaultColor });
  }

  return {
    acmevChartData: acmevData,
    acrtChartData: acrtData
  };
};

export const getChartConfigs = (variant: 'primary' | 'secondary' = 'primary') => {
  const primaryColor = variant === 'primary' ? '#7663EB' : '#32ADE6';
  const acmevChartConfig = {
    remaining: {
      label: 'Remaining Budget',
      color: primaryColor
    },
    spent: {
      label: 'Budget Spent',
      color: '#FF2D55'
    }
  } satisfies ChartConfig;

  const acrtChartConfig = {
    target: {
      label: 'Revenue Target',
      color: primaryColor
    },
    achieved: {
      label: 'Achieved Revenue',
      color: '#34C759'
    }
  } satisfies ChartConfig;

  return { acmevChartConfig, acrtChartConfig };
};
